import React from "react";
import { FiBell, FiMail } from "react-icons/fi";
import { COMPOSE_MODES } from "./broadcastEnums";

/**
 * The first decision in the composer: a popup, or an email.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS TWO TILES AND NOT THREE PILLS
 * ---------------------------------------------------------------------------
 * It used to be a pill row — "Push only / Email only / Push + email" — sitting
 * two thirds of the way down the page, next to the audience. That put the most
 * consequential choice on the screen below the fields it governs, at the same
 * visual weight as picking a segment, and offered a third option that made both
 * surfaces write one compromised message.
 *
 * Now it leads, and it is sized like what it is: a fork in the road. Each tile
 * carries the accent its mode uses everywhere else — warm for the popup, ink
 * for the email — so the moment after choosing, the form underneath is
 * recognisably the thing that was chosen.
 *
 * A real radio group, so arrow keys and the "one of two" announcement come from
 * the platform.
 */

/** Full class strings only. Tailwind's purger cannot see constructed names. */
const TILE_BASE =
  "flex flex-1 items-start gap-3 rounded-[10px] border p-4 text-left transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

const TILE_UNSELECTED =
  "border-bc-border bg-white hover:border-bc-soft focus-visible:ring-bc-soft";

/**
 * The selected treatment, per mode. This is the seed of each mode's look: the
 * popup half of the composer is warm and the email half is ink, and both start
 * here.
 */
const TILE_SELECTED = {
  push: "border-orange-primary bg-light-orange focus-visible:ring-orange-primary",
  email: "border-bc-text bg-bc-neut-bg focus-visible:ring-bc-text",
};

const ICON_BASE =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border";

const ICON_UNSELECTED = "border-bc-border bg-bc-subtle text-bc-faint";

const ICON_SELECTED = {
  push: "border-transparent bg-orange-primary text-white",
  email: "border-transparent bg-bc-text text-white",
};

const TITLE_SELECTED = {
  push: "text-bc-accent-text",
  email: "text-bc-text",
};

const ICONS = { push: FiBell, email: FiMail };

/**
 * One mode tile.
 *
 * @param {Object} props
 * @param {{ id: string, label: string, tagline: string }} props.mode
 * @param {boolean} props.selected
 * @param {() => void} props.onSelect
 * @returns {React.ReactElement}
 */
const ModeTile = ({ mode, selected, onSelect }) => {
  const Icon = ICONS[mode.id] || FiBell;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-testid={`bc-mode-${mode.id}`}
      className={`${TILE_BASE} ${
        selected ? TILE_SELECTED[mode.id] : TILE_UNSELECTED
      }`}
    >
      <span
        aria-hidden="true"
        className={`${ICON_BASE} ${
          selected ? ICON_SELECTED[mode.id] : ICON_UNSELECTED
        }`}
      >
        <Icon />
      </span>
      <span className="min-w-0">
        <span
          className={`block text-[15px] font-semibold ${
            selected ? TITLE_SELECTED[mode.id] : "text-bc-text"
          }`}
        >
          {mode.label}
        </span>
        <span className="mt-0.5 block text-xs text-bc-soft">{mode.tagline}</span>
      </span>
    </button>
  );
};

/**
 * The popup-or-email switch.
 *
 * @param {Object} props
 * @param {string} props.value - The current `channels` value.
 * @param {(id: string) => void} props.onChange - Called with the chosen mode id.
 * @param {string} [props.className] - Extra classes on the wrapper, merged last.
 * @returns {React.ReactElement}
 */
export const ComposeModeSwitch = ({ value, onChange, className = "" }) => (
  <div
    role="radiogroup"
    aria-label="What are you sending?"
    className={`flex flex-wrap gap-3 ${className}`}
  >
    {COMPOSE_MODES.map((mode) => (
      <ModeTile
        key={mode.id}
        mode={mode}
        selected={value === mode.id}
        onSelect={() => onChange?.(mode.id)}
      />
    ))}
  </div>
);

export default ComposeModeSwitch;
